import {
  ChevronDownIcon,
  ChevronUpIcon,
  QuestionOutlineIcon,
} from '@chakra-ui/icons';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Center,
  Flex,
  HStack,
  Link,
  Text,
  Textarea,
  Tooltip,
  useColorModeValue,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Collapse } from '@chakra-ui/transition';
import NextLink from 'next/link';
import React, {
  ChangeEventHandler,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Article, User } from '../common/libs/article';
import { BadgeArgs } from '../common/libs/badgeType';
import { commentDiscloseScope } from '../common/libs/commentDiscloseScope';
import { sendAnalyticsEvent } from '../common/libs/googleAnalytics';
import { createUserName } from '../common/libs/user';
import { useAppContext } from '../contexts/app';
import {
  ArticleComment,
  ArticleCommentReply,
} from '../hooks/useArticleComment';
import useIdApiClient from '../hooks/useIdApiClient';
import { IoEyeOff, IoReload } from './Icons';
import LinkifyText from './LinkifyText';
import { ProfileImage } from './ProfileImage';

function DateLabel({
  dateTime,
  style,
}: {
  dateTime: string;
  style?: React.CSSProperties | undefined;
}) {
  const date = new Date(dateTime);
  const passedDay = Math.floor(
    (new Date().getTime() - date.getTime()) / (1000 * 60 * 60 * 24),
  );

  const dateToStr = (date: Date) => {
    let format = 'YYYY/MM/DD hh:mm';
    format = format.replace(/YYYY/g, date.getFullYear().toString());
    format = format.replace(/MM/g, ('0' + (date.getMonth() + 1)).slice(-2));
    format = format.replace(/DD/g, ('0' + date.getDate()).slice(-2));
    format = format.replace(/hh/g, ('0' + date.getHours()).slice(-2));
    format = format.replace(/mm/g, ('0' + date.getMinutes()).slice(-2));
    format = format.replace(/ss/g, ('0' + date.getSeconds()).slice(-2));
    return format;
  };

  let updateDateStr;
  if (passedDay < 1) {
    const passedHour = Math.floor(
      (new Date().getTime() - date.getTime()) / (1000 * 60 * 60),
    );
    if (passedHour < 1) {
      const passedMin = Math.floor(
        (new Date().getTime() - date.getTime()) / (1000 * 60),
      );
      updateDateStr = `${passedMin < 0 ? 0 : passedMin} 分前`;
    } else {
      updateDateStr = `${passedHour} 時間前`;
    }
  } else if (passedDay <= 7) {
    updateDateStr = `${passedDay} 日前`;
  } else {
    updateDateStr = `${date.getFullYear()}/${
      date.getMonth() + 1
    }/${date.getDate()}`;
  }

  return (
    <Tooltip placement={'top'} bgColor={'gray'} label={dateToStr(date)}>
      <Text style={style}>{updateDateStr}</Text>
    </Tooltip>
  );
}

function ArticleCommentReply({
  loginUserId,
  comment,
  reply,
  deleteArticleCommentReply,
  fetchArticleComments,
}: {
  loginUserId: number | null;
  comment: ArticleComment;
  reply: ArticleCommentReply;
  deleteArticleCommentReply: (
    commentId: number,
    replyId: number,
  ) => Promise<void>;
  fetchArticleComments: () => Promise<void>;
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const canDelete =
    !!loginUserId && !reply.isDeleted && reply.user?.userId === loginUserId;
  const deleteCommentReply = () => {
    deleteArticleCommentReply(comment.id, reply.id)
      .then(() => {
        fetchArticleComments();
      })
      .catch(() => {});
  };
  const commentColor = useColorModeValue('gray.50', 'whiteAlpha.300');
  const nameFontColor = useColorModeValue('gray.600', 'whiteAlpha.700');
  const deleteButtonColor = useColorModeValue('gray.300', 'gray.700');

  return (
    <Flex align="flex-start" mb={2}>
      <ProfileImage
        userId={reply.user?.userId}
        discloseId={reply.user.discloseId}
        updateDateEpochMilliseconds={reply.user?.updateDateEpochMilliseconds}
        hasProfileImage={reply.user?.hasProfileImage}
        idBaseProfileUrl={reply.user.idBaseProfileUrl}
        size="sm"
        mt={1}
        onClick={() =>
          sendAnalyticsEvent('comment_reply', 'click', 'profile_img')
        }
      />
      <Box
        ms={2}
        bgColor={commentColor}
        w={'94%'}
        borderRadius={8}
        ml={4}
        p={2}
      >
        <Flex align="baseline" justifyContent={'space-between'} mb={1}>
          <HStack>
            {!reply.isDeleted && (
              <NextLink
                href={`/profile/${reply.user.discloseId || reply.user.userId}`}
                passHref
              >
                <Link
                  onClick={() =>
                    sendAnalyticsEvent('comment_reply', 'click', 'user_name')
                  }
                  fontSize="sm"
                  fontWeight="bold"
                  textColor={nameFontColor}
                >
                  {createUserName(reply.user)}
                </Link>
              </NextLink>
            )}
            <DateLabel
              dateTime={reply.createDate}
              style={{ fontSize: '10px' }}
            />
          </HStack>
          {canDelete && (
            <Button
              size="xs"
              variant="ghost"
              color={deleteButtonColor}
              fontSize={12}
              h={6}
              w={6}
              pl={5}
              pr={5}
              onClick={onOpen}
            >
              削除
            </Button>
          )}
        </Flex>
        {reply.message.split(/\r?\n/).map((line, i) => (
          <LinkifyText
            key={i}
            fontSize={reply.isDeleted ? 'sm' : 'inherit'}
            gaEventLabelPrefix={`article-comment-reply`}
          >
            {line}
          </LinkifyText>
        ))}
      </Box>
      <DeleteConfirmDialog
        title="返信"
        isOpen={isOpen}
        onClose={onClose}
        onDelete={deleteCommentReply}
      />
    </Flex>
  );
}

function DeleteConfirmDialog({
  title,
  isOpen,
  onClose,
  onDelete,
}: {
  title: string;
  isOpen: boolean;
  onClose: () => void;
  onDelete: () => void;
}) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  return (
    <AlertDialog
      motionPreset="slideInBottom"
      leastDestructiveRef={cancelRef}
      onClose={onClose}
      isOpen={isOpen}
      isCentered
    >
      <AlertDialogOverlay />

      <AlertDialogContent>
        <AlertDialogHeader>{`${title}削除`}</AlertDialogHeader>
        <AlertDialogCloseButton />
        <AlertDialogBody>削除してよろしいですか？</AlertDialogBody>
        <AlertDialogFooter>
          <Button ref={cancelRef} onClick={onClose}>
            キャンセル
          </Button>
          <Button
            colorScheme="red"
            ml={3}
            onClick={() => {
              onDelete();
              onClose();
            }}
          >
            削除
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function ArticleCommentInput({
  loginUserId,
  article,
  postArticleComment,
  getArticleCommentCount,
  fetchArticleComments,
  isOpen,
  onToggle,
  setBadge,
  achievedBadgeModalOnOpen,
  isDemoAccount,
}: {
  loginUserId: number | null;
  article: Article;
  postArticleComment: (message: string) => Promise<Array<BadgeArgs>>;
  getArticleCommentCount: () => Promise<void>;
  fetchArticleComments: () => Promise<void>;
  isOpen: boolean;
  onToggle: () => void;
  setBadge: (badge: BadgeArgs) => void;
  achievedBadgeModalOnOpen: () => void;
  isDemoAccount: boolean;
}) {
  const { proceedIdPage } = useIdApiClient();
  const { isAuthenticated, isLicenseVerified } = useAppContext();
  const [message, setMessage] = useState('');
  const [canPostComment, setCanPostComment] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  useEffect(() => {
    setMessage('');
    setCanPostComment(false);
  }, [article.articleId]);

  const toast = useToast();

  const enableComment = !!loginUserId && !isDemoAccount && isLicenseVerified;

  const placeHolder =
    !loginUserId || (isAuthenticated && !isLicenseVerified)
      ? ''
      : isDemoAccount
      ? 'デモアカウントのため、投稿できません'
      : 'コメントを入力する...';

  const onChange: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    setMessage(e.target.value);
    setCanPostComment(!!e.target.value && e.target.value.length > 0);
  };
  const postComment = () => {
    postArticleComment(message)
      .then((grantedBadges) => {
        setMessage('');
        setCanPostComment(false);
        getArticleCommentCount();
        fetchArticleComments();
        if (!isOpen) {
          onToggle();
        }
        if (grantedBadges.length > 0) {
          setBadge(grantedBadges[grantedBadges.length - 1]);
          sendAnalyticsEvent('badge', 'achieved', 'comment');
          achievedBadgeModalOnOpen();
        }
      })
      .catch(() => {
        toast({
          title: 'コメントの送信に失敗しました。',
          description: 'しばらく待って再度お試しください。',
          status: 'error',
          isClosable: true,
          variant: 'subtle',
        });
      });
  };
  const onFocus = () => {
    setIsFocused(true);
  };
  const onBlur = () => {
    setIsFocused(false);
  };
  const postButtonColor = enableComment ? 'brand.500' : 'gray.400';
  const inputAreaColor = useColorModeValue('white', 'blackAlpha.300');
  const inputFontColor = useColorModeValue('black', 'white');
  const postButtonBgColor = useColorModeValue('white', 'blackAlpha.600');
  const loginUrl = useMemo(() => {
    const redirectUrl = encodeURIComponent(
      `/article/view/${article.articleId}`,
    );
    return `/login?redirectUrl=${redirectUrl}`;
  }, [article]);
  const isButtonsVisible = isFocused || message.length > 0;

  return (
    <Flex flexDir="column" align="flex-end" pos="relative" gap={1}>
      {!loginUserId && (
        <Box pos="absolute" left={2} top={2} zIndex="docked">
          <NextLink href={loginUrl} passHref>
            <Link
              fontSize={'sm'}
              variant="ghost"
              color="brand.400"
              onClick={() =>
                sendAnalyticsEvent('induction', 'login', 'from_comment_input')
              }
              aria-label="コメントするにはログインしてください"
            >
              {`コメントするにはログインしてください`}
            </Link>
          </NextLink>
        </Box>
      )}
      {isAuthenticated && !isLicenseVerified && (
        <Box pos="absolute" left={2} top={2} zIndex="docked">
          <Link
            fontSize={'sm'}
            variant="ghost"
            color="brand.400"
            onClick={async () => {
              // prettier-ignore
              sendAnalyticsEvent('induction', 'doctor_certification', 'from_comment_input');
              await proceedIdPage(`/users/doctor/register?from_service=slide`);
            }}
            aria-label="コメントするには医師/医学生認証してください"
          >
            {`コメントするには医師/医学生認証してください`}
          </Link>
        </Box>
      )}
      <Textarea
        aria-label="コメント"
        size="sm"
        placeholder={placeHolder}
        color={inputFontColor}
        bgColor={inputAreaColor}
        w={'100%'}
        borderRadius={8}
        borderColor={'darkgray'}
        p={2}
        value={message}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        isDisabled={!enableComment}
        maxLength={1024}
        rows={2}
      />
      <HStack spacing={1} hidden={!isButtonsVisible}>
        <Button
          size="sm"
          variant="outline"
          color={postButtonColor}
          bgColor={postButtonBgColor}
          borderColor={postButtonColor}
          borderRadius={20}
          fontSize={12}
          h={8}
          px={6}
          onClick={postComment}
          isDisabled={!canPostComment}
        >
          コメントする
        </Button>
      </HStack>
    </Flex>
  );
}

function ArticleCommentReplyInput({
  loginUserId,
  article,
  commentId,
  postArticleCommentReply,
  fetchArticleComments,
  setBadge,
  achievedBadgeModalOnOpen,
  isDemoAccount,
}: {
  loginUserId: number | null;
  article: Article;
  commentId: number;
  postArticleCommentReply: (
    commentId: number,
    message: string,
  ) => Promise<Array<BadgeArgs>>;
  fetchArticleComments: () => Promise<void>;
  setBadge: (badge: BadgeArgs) => void;
  achievedBadgeModalOnOpen: () => void;
  isDemoAccount: boolean;
}) {
  const { isAuthenticated, isLicenseVerified } = useAppContext();
  const [message, setMessage] = useState('');
  const [canPostCommentReply, setCanPostCommentReply] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  useEffect(() => {
    setMessage('');
    setCanPostCommentReply(false);
  }, [article.articleId]);

  const toast = useToast();

  const enableComment = !!loginUserId && !isDemoAccount && isLicenseVerified;

  const placeHolder = !loginUserId
    ? '返信するにはログインして下さい'
    : isDemoAccount
    ? 'デモアカウントのため、返信できません'
    : isAuthenticated && !isLicenseVerified
    ? '返信するには医師/医学生認証して下さい'
    : ' 返信を入力する...';
  const onChange: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    setMessage(e.target.value);
    setCanPostCommentReply(!!e.target.value && e.target.value.length > 0);
  };
  const postCommentReply = () => {
    postArticleCommentReply(commentId, message)
      .then((grantedBadges) => {
        setMessage('');
        setCanPostCommentReply(false);
        fetchArticleComments();
        if (grantedBadges.length > 0) {
          setBadge(grantedBadges[grantedBadges.length - 1]);
          sendAnalyticsEvent('badge', 'achieved', 'comment_reply');
          achievedBadgeModalOnOpen();
        }
      })
      .catch(() => {
        toast({
          title: 'コメントの送信に失敗しました。',
          description: 'しばらく待って再度お試しください。',
          status: 'error',
          isClosable: true,
          variant: 'subtle',
        });
      });
  };
  const onFocus = () => {
    setIsFocused(true);
  };
  const onBlur = () => {
    setIsFocused(false);
  };
  const postButtonColor = enableComment ? 'brand.500' : 'gray.400';
  const inputAreaColor = useColorModeValue('white', 'blackAlpha.300');
  const inputFontColor = useColorModeValue('black', 'white');
  const postButtonBgColor = useColorModeValue('white', 'blackAlpha.600');
  const isButtonsVisible = isFocused || message.length > 0;

  return (
    <Flex align="flex-end" flexDir="column" gap={1}>
      <Textarea
        size="sm"
        placeholder={placeHolder}
        color={inputFontColor}
        bgColor={inputAreaColor}
        w={'100%'}
        borderRadius={8}
        borderColor={'darkgray'}
        p={2}
        value={message}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        isDisabled={!enableComment}
        maxLength={1024}
        rows={2}
      />
      <HStack spacing={2} hidden={!isButtonsVisible}>
        <Button
          size="sm"
          variant="outline"
          color={postButtonColor}
          bgColor={postButtonBgColor}
          borderColor={postButtonColor}
          borderRadius={20}
          fontSize={12}
          h={8}
          px={6}
          onClick={postCommentReply}
          isDisabled={!canPostCommentReply}
        >
          返信する
        </Button>
      </HStack>
    </Flex>
  );
}

function ArticleComment({
  loginUserId,
  comment,
  deleteArticleComment,
  fetchArticleComments,
}: {
  loginUserId: number | null;
  comment: ArticleComment;
  deleteArticleComment: (commentId: number) => Promise<void>;
  fetchArticleComments: () => Promise<void>;
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const canDelete =
    !!loginUserId && !comment.isDeleted && comment.user?.userId === loginUserId;
  const deleteComment = () => {
    deleteArticleComment(comment.id)
      .then(() => {
        fetchArticleComments();
      })
      .catch(() => {});
  };
  const commentColor = useColorModeValue('gray.50', 'whiteAlpha.300');
  const nameFontColor = useColorModeValue('gray.600', 'whiteAlpha.700');
  const deleteButtonColor = useColorModeValue('gray.300', 'gray.700');

  return (
    <Flex align="flex-start" mt={2} mb={2}>
      <ProfileImage
        userId={comment.user?.userId}
        discloseId={comment.user.discloseId}
        updateDateEpochMilliseconds={comment.user?.updateDateEpochMilliseconds}
        hasProfileImage={comment.user?.hasProfileImage}
        idBaseProfileUrl={comment.user.idBaseProfileUrl}
        size="sm"
        mt={1}
        onClick={() => sendAnalyticsEvent('comment', 'click', 'profile_img')}
      />
      <Box
        ms={2}
        bgColor={commentColor}
        w={'94%'}
        borderRadius={8}
        ml={4}
        p={2}
      >
        <Flex align="baseline" justifyContent={'space-between'} mb={1}>
          <HStack>
            {!comment.isDeleted && (
              <NextLink
                href={`/profile/${
                  comment.user.discloseId || comment.user.userId
                }`}
                passHref
              >
                <Link
                  onClick={() =>
                    sendAnalyticsEvent('comment', 'click', 'user_name')
                  }
                  fontSize="sm"
                  fontWeight="bold"
                  textColor={nameFontColor}
                >
                  {createUserName(comment.user)}
                </Link>
              </NextLink>
            )}
            <DateLabel
              dateTime={comment.createDate}
              style={{ fontSize: '10px' }}
            />
            {comment.discloseScope === commentDiscloseScope.PRIVATE && (
              <>
                <IoEyeOff color="gray" size={16} />
                <Tooltip
                  placement="top"
                  bgColor="gray"
                  label={
                    <Text>
                      このコメントは、コメント投稿者と
                      <br />
                      スライド投稿者にのみ表示されます。
                    </Text>
                  }
                >
                  <QuestionOutlineIcon w={3} h={3} color="gray" />
                </Tooltip>
              </>
            )}
          </HStack>
          {canDelete && (
            <Button
              size="xs"
              variant="ghost"
              color={deleteButtonColor}
              fontSize={12}
              h={6}
              w={6}
              pl={5}
              pr={5}
              onClick={onOpen}
            >
              削除
            </Button>
          )}
        </Flex>
        {comment.message.split(/\r?\n/).map((line, i) => (
          <LinkifyText
            key={i}
            fontSize={comment.isDeleted ? 'sm' : 'inherit'}
            gaEventLabelPrefix={`article-comment`}
          >
            <Text color="gray" as="span">
              {i === 0 && comment.page && `[ ${comment.page}ページ目 ] `}
            </Text>
            {line}
          </LinkifyText>
        ))}
      </Box>
      <DeleteConfirmDialog
        title="コメント"
        isOpen={isOpen}
        onClose={onClose}
        onDelete={deleteComment}
      />
    </Flex>
  );
}

function ArticleComments({
  loginUserId,
  article,
  articleCommentCount,
  articleComments,
  getArticleCommentCount,
  fetchArticleComments,
  postArticleComment,
  deleteArticleComment,
  postArticleCommentReply,
  deleteArticleCommentReply,
  isDemoAccount,
  isDoctorRecruiterAccount,
  achievedBadgeModalOnOpen,
  setBadge,
}: {
  loginUserId: number | null;
  article: Article;
  articleCommentCount: number;
  articleComments: ArticleComment[];
  getArticleCommentCount: () => Promise<void>;
  fetchArticleComments: () => Promise<void>;
  postArticleComment: (message: string) => Promise<Array<BadgeArgs>>;
  deleteArticleComment: (commentId: number) => Promise<void>;
  postArticleCommentReply: (
    commentId: number,
    message: string,
  ) => Promise<Array<BadgeArgs>>;
  deleteArticleCommentReply: (
    commentId: number,
    replyId: number,
  ) => Promise<void>;
  isDemoAccount: boolean;
  isDoctorRecruiterAccount: boolean;
  achievedBadgeModalOnOpen: () => void;
  setBadge: React.Dispatch<React.SetStateAction<BadgeArgs | undefined>>;
}) {
  const { proceedIdPage } = useIdApiClient();
  const { isAuthenticated, isLicenseVerified } = useAppContext();
  const { isOpen, onToggle } = useDisclosure();
  const loginUrl = useMemo(() => {
    const redirectUrl = encodeURIComponent(
      `/article/view/${article.articleId}`,
    );
    return `/login?redirectUrl=${redirectUrl}`;
  }, [article]);
  const commentAreaColor = useColorModeValue('white', 'whiteAlpha.300');
  const reloadButtonColor = useColorModeValue('gray', 'lightgray');
  const loginButtonColor = useColorModeValue('white', 'blackAlpha.600');
  const {
    isOpen: isOpenTooltip,
    onOpen: onOpenTooltip,
    onClose: onCloseTooltip,
  } = useDisclosure();
  const onClickReload = async () => {
    try {
      await getArticleCommentCount();
      await fetchArticleComments();
      onOpenTooltip();
    } catch {}
  };

  const collapseProps = {
    startingHeight: articleCommentCount > 0 ? 210 : 150,
    endingHeight: 'auto',
  };

  return (
    <Box
      borderRadius="lg"
      bgColor={commentAreaColor}
      borderColor={'blackAlpha.300'}
      borderWidth={1}
    >
      <Collapse
        startingHeight={collapseProps.startingHeight}
        endingHeight={collapseProps.endingHeight}
        in={isOpen}
        style={{ paddingBottom: '1rem' }}
      >
        <HStack alignItems={'baseline'} p={3}>
          <VStack alignItems={'flex-start'}>
            <Text fontSize="md" fontWeight="bold">
              投稿された先生へ質問や勉強になったポイントをコメントしてみましょう！
            </Text>
            <Text
              fontSize={'14px'}
            >{`${articleCommentCount} 件のコメント`}</Text>
          </VStack>
          <Tooltip
            label="コメントを再読み込みしました"
            placement="right"
            isOpen={isOpenTooltip}
            onClose={onCloseTooltip}
            closeDelay={2000}
          >
            <Button
              aria-label="コメントを再読み込みする"
              size={'sm'}
              variant="ghost"
              pl={-3}
              rightIcon={<IoReload size={14} color={reloadButtonColor} />}
              onClick={() => onClickReload()}
            />
          </Tooltip>
        </HStack>
        <VStack align="stretch" spacing={2} px={3}>
          {!isDoctorRecruiterAccount && (
            <ArticleCommentInput
              loginUserId={loginUserId}
              article={article}
              getArticleCommentCount={getArticleCommentCount}
              fetchArticleComments={fetchArticleComments}
              postArticleComment={postArticleComment}
              isOpen={isOpen}
              onToggle={onToggle}
              setBadge={setBadge}
              achievedBadgeModalOnOpen={achievedBadgeModalOnOpen}
              isDemoAccount={isDemoAccount}
            />
          )}
          {articleComments.map((comment) => (
            <Box mb={1} key={comment.id}>
              <ArticleComment
                loginUserId={loginUserId}
                comment={comment}
                deleteArticleComment={deleteArticleComment}
                fetchArticleComments={fetchArticleComments}
              />
              {!comment.isDeleted && !!loginUserId && isLicenseVerified && (
                <Box
                  ml={4}
                  pl={4}
                  borderLeftWidth={1}
                  borderLeftColor="lightgray"
                >
                  {comment.replies.map((reply) => (
                    <ArticleCommentReply
                      key={reply.id}
                      loginUserId={loginUserId}
                      comment={comment}
                      reply={reply}
                      deleteArticleCommentReply={deleteArticleCommentReply}
                      fetchArticleComments={fetchArticleComments}
                    />
                  ))}
                  {(!isDoctorRecruiterAccount ||
                    loginUserId === article.authorId) && (
                    <ArticleCommentReplyInput
                      loginUserId={loginUserId}
                      article={article}
                      commentId={comment.id}
                      postArticleCommentReply={postArticleCommentReply}
                      fetchArticleComments={fetchArticleComments}
                      setBadge={setBadge}
                      achievedBadgeModalOnOpen={achievedBadgeModalOnOpen}
                      isDemoAccount={isDemoAccount}
                    />
                  )}
                </Box>
              )}
            </Box>
          ))}
        </VStack>
        {!loginUserId && articleCommentCount > 1 && (
          <Center pl={6} pr={6}>
            <NextLink href={loginUrl}>
              <Button
                size={'sm'}
                flexShrink={0}
                bgColor={loginButtonColor}
                color="brand.400"
                borderColor="brand.400"
                borderWidth={1}
                w={'100%'}
                onClick={() =>
                  sendAnalyticsEvent(
                    'induction',
                    'login',
                    'from_more_comment_button',
                  )
                }
              >
                {'続きを見るにはログインしてください'}
              </Button>
            </NextLink>
          </Center>
        )}
        {isAuthenticated && !isLicenseVerified && articleCommentCount > 1 && (
          <Center pl={6} pr={6}>
            <Button
              size={'sm'}
              flexShrink={0}
              bgColor={loginButtonColor}
              color="brand.400"
              borderColor="brand.400"
              borderWidth={1}
              w={'100%'}
              onClick={async () => {
                // prettier-ignore
                sendAnalyticsEvent('induction', 'doctor_certification', 'from_more_comment_button');
                await proceedIdPage(
                  `/users/doctor/register?from_service=slide`,
                );
              }}
            >
              {'続きを見るには医師/医学生認証してください'}
            </Button>
          </Center>
        )}
      </Collapse>
      <Button
        variant="ghost"
        rightIcon={isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
        fontSize="xs"
        textColor={'green'}
        w="100%"
        textAlign={'center'}
        onClick={() => {
          sendAnalyticsEvent('comment', 'click', 'collapse');
          onToggle();
        }}
      >
        {isOpen ? '閉じる' : 'コメントを見る'}
      </Button>
    </Box>
  );
}

export default ArticleComments;
